/**
 * useTriggerPasswordReset — TanStack mutation over
 * `POST /organizations/:slug/users/:userId/password-reset`.
 *
 * 202 success returns `{ sent_to: 'a***@example.com' }` — the BE
 * redacts the recipient email so the FE can surface "sent to ***" in
 * a confirmation toast without leaking the full address back to a
 * curious admin (defense-in-depth against shoulder-surfing).
 *
 * Error envelope:
 *   - 'forbidden'   — 403 (missing identity.users.reset_password)
 *   - 'notFound'    — 404 (anti-enum)
 *   - 'rateLimited' — 429 (per-admin rate limit)
 *   - 'error'       — anything else
 *
 * On success the audit feed for this user is invalidated — the BE
 * emits `user.password_reset_sent` and the Activity tab should
 * surface it on next render.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { buildUserAuditQueryKey } from './use-user-audit.js';

export interface PasswordResetResult {
  readonly sentTo: string;
}

export type PasswordResetError =
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'notFound' }
  | { readonly kind: 'rateLimited' }
  | { readonly kind: 'error' };

export interface UseTriggerPasswordResetResult {
  readonly mutateAsync: () => Promise<PasswordResetResult>;
  readonly isPending: boolean;
}

export function useTriggerPasswordReset(
  userId: string | undefined,
): UseTriggerPasswordResetResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const queryClient = useQueryClient();

  const mutation = useMutation<
    PasswordResetResult,
    PasswordResetError,
    undefined
  >({
    mutationFn: async () => {
      if (orgSlug === null || userId === undefined) {
        throw { kind: 'error' } satisfies PasswordResetError;
      }
      const { response, data } = await apiClient.POST(
        '/organizations/{slug}/users/{userId}/password-reset',
        {
          params: { path: { slug: orgSlug, userId } },
        },
      );
      if (response.status === 403) {
        throw { kind: 'forbidden' } satisfies PasswordResetError;
      }
      if (response.status === 404) {
        throw { kind: 'notFound' } satisfies PasswordResetError;
      }
      if (response.status === 429) {
        throw { kind: 'rateLimited' } satisfies PasswordResetError;
      }
      if (!response.ok || data === undefined) {
        throw { kind: 'error' } satisfies PasswordResetError;
      }
      const dto = data as { sent_to?: unknown };
      const sentTo = typeof dto.sent_to === 'string' ? dto.sent_to : '';
      return { sentTo };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: buildUserAuditQueryKey(orgSlug, userId),
      });
    },
  });

  return {
    mutateAsync: () => mutation.mutateAsync(undefined),
    isPending: mutation.isPending,
  };
}
